"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStudio } from "@/contexts/studio-context";
import { useActivePianoNotes } from "@/hooks/use-active-piano-notes";
import { useHintPublisher } from "@/hooks/use-hint-publisher";
import { Note } from "@/lib/music/note";
import {
  applyInversion,
  hslHex,
  type ChordEntry,
} from "@/lib/music/chord-catalog";
import { ROOT_PITCH_CLASS } from "@/lib/music/theory-helpers";
import { chordQualityToLayout } from "@/lib/virtuosa-layout/quality-mapper";
import type { Hint } from "@/contexts/hint-context";

const ADVANCE_DELAY_MS = 700;
const LISTEN_DURATION_MS = 1100;
const HINT_BASE_OCTAVE = 3;

type UseStaticValidationArgs = {
  entry: ChordEntry;
  /** Persistent set of validated inversion ids (controlled). */
  done?: ReadonlySet<string>;
  /** Called once when an inversion transitions from unvalidated to validated. */
  onMarkDone?: (invId: string) => void;
};

export type StaticValidationState = {
  effInv: number;
  positionId: string;
  maxInvIdx: number;
  invertedSemis: number[];
  requiredPcs: number[];
  hintMidis: number[];
  activePcs: ReadonlySet<number>;
  done: ReadonlySet<string>;
  justValidated: boolean;
  goPrev: () => void;
  goNext: () => void;
  goTo: (idx: number) => void;
  handleListen: () => void;
  handleRandom: () => void;
};

/**
 * Drives the static-mode practice grid: chord position cycling, inversion
 * book-keeping, octave-agnostic validation with bass constraint, "listen"
 * gesture, and hint pushdown to the studio piano.
 */
export function useStaticValidation({
  entry,
  done: doneProp,
  onMarkDone,
}: UseStaticValidationArgs): StaticValidationState {
  const { studio } = useStudio();
  const rootPc = useMemo(() => ROOT_PITCH_CLASS[entry.root], [entry.root]);
  const hintHex = useMemo(() => hslHex(260, 75, 65), []);

  const numNotes = entry.intervals.length;
  // Cap at 3 inversions max (per V7d spec, even for tetrads).
  const maxInvIdx = Math.min(numNotes - 1, 2);

  const [invIdx, setInvIdx] = useState(0);
  const [internalDone, setInternalDone] = useState<Set<string>>(new Set());
  const [justValidated, setJustValidated] = useState(false);
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isListeningRef = useRef(false);
  const listenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isControlled = doneProp !== undefined;
  const done = isControlled ? doneProp : internalDone;

  // Reset UI state on entry change.
  useEffect(() => {
    setInvIdx(0);
    setJustValidated(false);
    if (!isControlled) {
      setInternalDone(new Set());
    }

    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  }, [entry.id, isControlled]);

  const effInv = Math.min(invIdx, maxInvIdx);
  const positionId = `inv-${effInv}`;

  const invertedSemis = useMemo(
    () => applyInversion(entry.intervals, effInv),
    [entry.intervals, effInv],
  );

  const requiredPcs = useMemo(
    () => invertedSemis.map((s) => (((rootPc + s) % 12) + 12) % 12),
    [invertedSemis, rootPc],
  );

  const hintMidis = useMemo(
    () =>
      invertedSemis.map((s) => {
        const baseMidi = (HINT_BASE_OCTAVE + 1) * 12 + rootPc;

        return baseMidi + s;
      }),
    [invertedSemis, rootPc],
  );

  // ── Validation: pitch-class match (octave-agnostic) + bass constraint ──
  // The user can play in any octave, but the lowest note's pitch class must
  // match the expected bass of the current inversion. Otherwise root / 1st /
  // 2nd inversions would all validate identically (same set of PCs).
  const activeMidis = useActivePianoNotes();
  const activePcs = useMemo(() => {
    const set = new Set<number>();
    for (const m of activeMidis) {
      set.add(((m % 12) + 12) % 12);
    }

    return set;
  }, [activeMidis]);

  const lowestActivePc = useMemo<number | null>(() => {
    if (activeMidis.size === 0) {
      return null;
    }

    let lo = Infinity;
    for (const m of activeMidis) {
      if (m < lo) {
        lo = m;
      }
    }

    return ((lo % 12) + 12) % 12;
  }, [activeMidis]);

  const expectedBassPc = requiredPcs[0];

  const isHit = useMemo(() => {
    if (lowestActivePc === null) {
      return false;
    }

    if (lowestActivePc !== expectedBassPc) {
      return false;
    }

    return requiredPcs.every((pc) => activePcs.has(pc));
  }, [requiredPcs, activePcs, lowestActivePc, expectedBassPc]);

  const handleValidate = useCallback(() => {
    if (isListeningRef.current) {
      return;
    }

    if (isControlled) {
      onMarkDone?.(positionId);
    } else {
      setInternalDone((prev) => {
        if (prev.has(positionId)) {
          return prev;
        }

        const next = new Set(prev);
        next.add(positionId);

        return next;
      });
    }

    setJustValidated(true);

    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
    }

    advanceTimerRef.current = setTimeout(() => {
      setJustValidated(false);
      // Loop back to the first inversion after the last one.
      setInvIdx((i) => (i + 1 > maxInvIdx ? 0 : i + 1));
    }, ADVANCE_DELAY_MS);
  }, [isControlled, onMarkDone, positionId, maxInvIdx]);

  const wasHitRef = useRef(false);
  useEffect(() => {
    if (isHit && !wasHitRef.current) {
      handleValidate();
    }

    wasHitRef.current = isHit;
  }, [isHit, handleValidate]);

  // Publish hint into the shared HintContext (consumed by the studio
  // piano bridge and the FAB pill).
  const hint = useMemo<Hint | null>(() => {
    if (hintMidis.length === 0) {
      return null;
    }

    const layoutQuality = chordQualityToLayout(entry.quality);

    return {
      source: "chord-practice/static",
      chord:
        layoutQuality !== null
          ? { rootSemi: rootPc, quality: layoutQuality }
          : undefined,
      voicing: hintMidis,
      midis: hintMidis.map((midi) => ({ midi, color: hintHex })),
    };
  }, [hintMidis, hintHex, entry.quality, rootPc]);
  useHintPublisher(hint);

  // Cleanup timers on unmount.
  useEffect(() => {
    return () => {
      if (advanceTimerRef.current) {
        clearTimeout(advanceTimerRef.current);
      }

      if (listenTimerRef.current) {
        clearTimeout(listenTimerRef.current);
      }
    };
  }, []);

  const goPrev = useCallback(() => {
    setJustValidated(false);
    setInvIdx((i) => (i > 0 ? i - 1 : maxInvIdx));
  }, [maxInvIdx]);

  const goNext = useCallback(() => {
    setJustValidated(false);
    setInvIdx((i) => (i + 1 > maxInvIdx ? 0 : i + 1));
  }, [maxInvIdx]);

  const goTo = useCallback((idx: number) => {
    setJustValidated(false);
    setInvIdx(idx);
  }, []);

  const handleListen = useCallback(() => {
    if (!studio) {
      return;
    }

    const piano = studio.getPiano();
    const notes = hintMidis.map((m) => Note.fromMidi(m));

    isListeningRef.current = true;
    if (listenTimerRef.current) {
      clearTimeout(listenTimerRef.current);
    }

    for (const n of notes) {
      piano.press(n, 0.55, "app");
    }

    listenTimerRef.current = setTimeout(() => {
      for (const n of notes) {
        piano.release(n, "app");
      }

      setTimeout(() => {
        isListeningRef.current = false;
      }, 50);
    }, LISTEN_DURATION_MS);
  }, [studio, hintMidis]);

  const handleRandom = useCallback(() => {
    // Pick a random different inversion within the available range.
    if (maxInvIdx < 1) {
      return;
    }

    let next = invIdx;
    while (next === invIdx) {
      next = Math.floor(Math.random() * (maxInvIdx + 1));
    }

    goTo(next);
  }, [invIdx, maxInvIdx, goTo]);

  return {
    effInv,
    positionId,
    maxInvIdx,
    invertedSemis,
    requiredPcs,
    hintMidis,
    activePcs,
    done,
    justValidated,
    goPrev,
    goNext,
    goTo,
    handleListen,
    handleRandom,
  };
}
